import "./index.scss";

import { Box, Button as MButton, ButtonGroup, Grid, Link, MenuItem, Stack } from "@mui/material";
import { GridCellParams, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { ReactComponent as IconWorkoutTemplateOn } from "assets/images/icons/last-plan-change.svg";
import { ReactComponent as IconLastWorkoutSession } from "assets/images/icons/last-workout-session.svg";
import { ReactComponent as MailIcon } from "assets/images/square-buttons/mail.svg";
import Dropdown from "components/Dropdown";
import { PAGE_OPTIONS } from "components/Pagination";
import ScreenEmptySection from "components/ScreenEmptySection";
import Table from "components/Table";
import { DATE_FORMAT, MSG_OPERATION_FAILED } from "constants/index";
import { ReportTableOptions } from "containers/Reports/components/ReportTableOptions";
import { convertSecondsToHoursAndMinutes } from "helpers/utils";
import { TMemberDetails, TMemberTotalWorkoutStatistics } from "models/member";
import { WorkoutPlan } from "models/workoutplan";
import { TPagination } from "modules/api/type";
import {
  getMemberSummaryCardioAgainstGoal,
  getMemberSummaryStrengthAgainstGoal,
} from "modules/member/memberServices";
import moment from "moment";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import TimestampSummaryItem from "../TimestampSummaryItem";

const MemberSummaryComp = (props: {
  member: TMemberDetails;
  memberTotalWorkoutStatistics: TMemberTotalWorkoutStatistics;
  workoutPlan: WorkoutPlan;
  goalOptions: any[];
}) => {
  const { member, memberTotalWorkoutStatistics, workoutPlan, goalOptions } = props;
  const [cardioAgainstWhichGoal, setCardioAgainstWhichGoal] = useState<
    "TIME" | "HEART_RATE" | "CALORIES"
  >("TIME");
  const [cardioDateRange, setCardioDateRange] = useState<
    "THIS_MONTH" | "LAST_MONTH" | "TWO_MONTHS_AGO"
  >("THIS_MONTH");
  const [cardioDateRangeLabel, setCardioDateRangeLabel] = useState("");
  const [cardioGraphData, setCardioGraphData] = useState<any[]>([]);

  const [strengthAgainstWhichProgram, setStrengthAgainstWhichProgram] = useState("");
  const [strengthDateRange, setStrengthDateRange] = useState<
    "THIS_MONTH" | "LAST_MONTH" | "TWO_MONTHS_AGO"
  >("THIS_MONTH");
  const [strengthDateRangeLabel, setStrengthDateRangeLabel] = useState("");
  const [strengthGraphData, setStrengthGraphData] = useState<any[]>([]);
  const thisMonth = moment().format("MMMM YYYY");
  const lastMonth = moment().subtract(1, "M").format("MMMM YYYY");
  const twoMonthsAgo = moment().subtract(2, "M").format("MMMM YYYY");
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [pagination, setPagination] = useState<TPagination>({
    current_page: 0,
    data_from_offset: 0,
    data_to_offset: 0,
    per_page: PAGE_OPTIONS[0],
    total_pages: 0,
    total_records: 0,
  });

  useEffect(() => {
    switch (cardioDateRange) {
      case "THIS_MONTH":
        setCardioDateRangeLabel(thisMonth);
        break;
      case "LAST_MONTH":
        setCardioDateRangeLabel(lastMonth);
        break;
      case "TWO_MONTHS_AGO":
        setCardioDateRangeLabel(twoMonthsAgo);
        break;
    }

    if (member) {
      getMemberSummaryCardioAgainstGoal({
        against_goal: cardioAgainstWhichGoal,
        date_range: cardioDateRange,
        member_id: member.membership.member_id,
      })
        .then((res) => {
          let resultToReturn = [];
          //console.log("MemberSummary res is:\n", res);
          if (res.success) {
            let datesWithEntries = res.data
              .map((m) => moment(m.visit_date).date())
              .sort(function (a, b) {
                return a - b;
              });
            datesWithEntries[datesWithEntries.length] = getLastDayOfMonth(cardioDateRange);
            //console.log("MemberSummary datesWithEntries:\n", datesWithEntries);
            let length = datesWithEntries.length - 1;

            for (let i = 0; i <= datesWithEntries[length]; i++) {
              resultToReturn.push({
                cumulative: 0,
                daily: 0,
                name: i,
              });
            }
            let cumulative = 0;
            res.data?.forEach((e, index) => {
              let name = moment(e.visit_date).date();
              let value =
                cardioAgainstWhichGoal === "CALORIES"
                  ? e.calories
                  : cardioAgainstWhichGoal === "TIME"
                  ? e.time
                  : e.heart_rate;
              let entry = resultToReturn.find((f) => f.name === name);
              if (entry !== undefined) {
                entry["daily"] = entry["daily"] + value;

                if (index !== 0 && entry["cumulative"] === 0) {
                  entry["cumulative"] = cumulative;
                }
                if (index === 0) {
                  delete entry["cumulative"];
                }
                cumulative += value;
              }
            });
          } else {
            throw "error";
          }
          console.log(resultToReturn);
          setCardioGraphData(resultToReturn);
        })
        .catch((e) => {
          console.log(e);
          toast(MSG_OPERATION_FAILED, { type: "error" });
        });
    }
  }, [cardioAgainstWhichGoal, cardioDateRange, member?.membership?.member_id]);

  useEffect(() => {
    switch (strengthDateRange) {
      case "THIS_MONTH":
        setStrengthDateRangeLabel(thisMonth);
        break;
      case "LAST_MONTH":
        setStrengthDateRangeLabel(lastMonth);
        break;
      case "TWO_MONTHS_AGO":
        setStrengthDateRangeLabel(twoMonthsAgo);
        break;
    }

    if (member) {
      getMemberSummaryStrengthAgainstGoal({
        date_range: strengthDateRange,
        member_id: member.membership.member_id,
        strength_id: strengthAgainstWhichProgram === "-1" ? "" : strengthAgainstWhichProgram,
      })
        .then((res) => {
          let resultToReturn = [];
          if (res.success) {
            let datesWithEntries = res.data
              .map((m) => moment(m.visit_date).date())
              .sort(function (a, b) {
                return a - b;
              });
            datesWithEntries[datesWithEntries.length] = getLastDayOfMonth(strengthDateRange);
            //console.log("MemberSummary datesWithEntries.new:\n", datesWithEntries);
            let length = datesWithEntries.length - 1;
            for (let i = 0; i <= datesWithEntries[length]; i++) {
              resultToReturn.push({
                cumulative: 0,
                daily: 0,
                name: i,
              });
            }
            let cumulative = 0;
            res.data?.forEach((e, index) => {
              let name = moment(e.visit_date).date();
              let value = e.weight;
              let entry = resultToReturn.find((f) => f.name === name);
              if (entry !== undefined) {
                entry["daily"] = entry["daily"] + value;

                if (index !== 0 && entry["cumulative"] === 0) {
                  entry["cumulative"] = cumulative;
                }
                if (index === 0) {
                  delete entry["cumulative"];
                }
                cumulative += value;
              }
            });
          } else {
            throw "error";
          }
          setStrengthGraphData(resultToReturn);
        })
        .catch((e) => {
          console.log(e);
          toast(MSG_OPERATION_FAILED, { type: "error" });
        });
    }
  }, [strengthAgainstWhichProgram, strengthDateRange, member?.membership?.member_id]);

  useEffect(() => {
    //console.log("Here");
    //console.log("MemberSummary: memberTotalWorkoutStatistics", memberTotalWorkoutStatistics);
    if (memberTotalWorkoutStatistics) {
      const thisMonthCvTime = convertSecondsToHoursAndMinutes(
        memberTotalWorkoutStatistics?.total_workout_statistics[0].this_month.cv_time || 0
      );
      const lastMonthCvTime = convertSecondsToHoursAndMinutes(
        memberTotalWorkoutStatistics?.total_workout_statistics[1].last_month.cv_time || 0
      );
      const lastFullMonthCvTime = convertSecondsToHoursAndMinutes(
        memberTotalWorkoutStatistics?.total_workout_statistics[2].last_full_month.cv_time || 0
      );

      setRows([
        {
          id: 0,
          lastFullMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[2].last_full_month.wo_sessions ||
            0,
          lastMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[1].last_month.wo_sessions || 0,
          name: "WO Sessions",
          thisMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[0].this_month.wo_sessions || 0,
        },
        {
          id: 1,
          lastFullMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[2].last_full_month.calories || 0,
          lastMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[1].last_month.calories || 0,
          name: "Calories",
          thisMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[0].this_month.calories || 0,
        },
        {
          id: 2,
          lastFullMonth: lastFullMonthCvTime,
          lastMonth: lastMonthCvTime,
          name: "CV Time",
          thisMonth: thisMonthCvTime,
        },
        {
          id: 3,
          lastFullMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[2].last_full_month.weight || 0,
          lastMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[1].last_month.weight || 0,
          name: "Weight",
          thisMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[0].this_month.weight || 0,
        },
        {
          id: 4,
          lastFullMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[2].last_full_month.fitpoints ||
            0,
          lastMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[1].last_month.fitpoints || 0,
          name: "Fit Points",
          thisMonth:
            memberTotalWorkoutStatistics?.total_workout_statistics[0].this_month.fitpoints || 0,
        },
      ]);
    }
  }, [memberTotalWorkoutStatistics]);

  const today = new Date();
  const columns: GridColDef[] = [
    {
      align: "left",
      cellClassName: (params) => getCellClassName(params, ""),
      field: "name",
      flex: 0.4,
      headerAlign: "left",
      headerName: "",
      sortable: false,
      type: "number",
    },
    {
      cellClassName: (params) => getCellClassName(params, "MuiDataGrid-cell--textCenter"),
      field: "thisMonth",
      flex: 0.2,
      headerAlign: "center",
      headerName: `${moment(today).format("MMM 1-DD")}`,
      sortable: false,
      type: "number",
    },
    {
      cellClassName: (params) => getCellClassName(params, "MuiDataGrid-cell--textCenter"),
      field: "lastMonth",
      flex: 0.2,
      headerAlign: "center",
      headerName: `${moment(today).subtract(1, "month").format("MMM 1-DD")}`,
      sortable: false,
      type: "number",
    },
    {
      cellClassName: (params) => getCellClassName(params, "MuiDataGrid-cell--textCenter"),
      field: "lastFullMonth",
      flex: 0.2,
      headerAlign: "center",
      headerName: `All of ${moment(today).subtract(1, "month").format("MMM 'YY")}`,
      sortable: false,
      type: "number",
    },
  ];

  const getCellClassName = (params: GridCellParams<any, any, any>, cellClass?: string) => {
    cellClass += " default-font-attributes";
    const isOddCell = (parseInt(params.id.toString()) + 1) % 2 !== 0;
    if (isOddCell) {
      cellClass += " workout-stat-odd-row";
    }

    if (params.colDef.headerName === "Total %") {
      cellClass += " last-cell";
    }

    return cellClass;
  };

  const getLastDayOfMonth = (strengthDateRange: string): number => {
    const date = new Date();
    let lastDay = 0;
    switch (strengthDateRange) {
      case "THIS_MONTH":
        lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        return lastDay;
      case "LAST_MONTH":
        lastDay = new Date(date.getFullYear(), date.getMonth(), 0).getDate();
        return lastDay;
      case "TWO_MONTHS_AGO":
        lastDay = new Date(date.getFullYear(), date.getMonth() - 1, 0).getDate();
        return lastDay;
    }
  };
  // const woDate = moment(member?.last_wop_session).format(DATE_FORMAT);
  // console.log("Last Workout Session is: ", member?.last_wop_session);
  // console.log("Last Workout Session formatted is: ", woDate);
  // console.log(`cardioGraphData is ${JSON.stringify(cardioGraphData)}`);

  return (
    <Box>
      <Grid container className="timestamp-summary-container">
        <Grid item xs={4}>
          <TimestampSummaryItem
            icon={<IconLastWorkoutSession />}
            title={`Last Workout Session`}
            date={member?.last_wop_session}
          />
        </Grid>
        <Grid item xs={4}>
          <TimestampSummaryItem
            icon={<MailIcon />}
            title={`Last Message Sent to Member`}
            date={member?.last_msg_sent}
          />
        </Grid>
        <Grid item xs={4}>
          <TimestampSummaryItem
            icon={<IconWorkoutTemplateOn />}
            title={`Last Plan Change`}
            date={member?.last_plan_change}
          />
        </Grid>
      </Grid>
      <Stack direction={"row"} spacing={2} mt={4}>
        <Box flex={1}>
          <label className="section-title">Plan Goals</label>
          <Box flex={1} className="plan-goals-container" mt={2}>
            {workoutPlan?.goals.length > 0 &&
              workoutPlan?.goals.map((e, i) => {
                const entry = goalOptions.find((f) => f.value === e);
                if (!entry) return null;
                return (
                  <Box className="plan-goals-item" key={`memberSummaryWorkoutPlanGoals${i}`}>
                    <p>{entry.label}</p>
                  </Box>
                );
              })}
            {workoutPlan?.goals.length === 0 && <ScreenEmptySection />}
          </Box>
        </Box>

        <Box flex={1}>
          <label className="section-title">Total Workout Statistics</label>
          <Box flex={1} mt={2}>
            <Table
              rows={rows}
              columns={columns}
              sortingMode="client"
              paginationMode="server"
              hideFooterRowCount={true}
              rowCount={5}
              loading={false}
              className="total-workout-statistics"
              page={pagination.current_page}
              slots={undefined}
              headerHeight={undefined}
              pageSize={undefined}
              rowsPerPageOptions={undefined}
              onPageChange={function (page: number): void {
                throw new Error("Function not implemented.");
              }}
              pagination={true}
            />
          </Box>
        </Box>
      </Stack>

      <Stack direction={"row"} spacing={2} mt={4} mb={1}>
        <Box flex={1}>
          <p className="section-title">Cardio Against Goal</p>
          <Box flexDirection="row" display="flex" justifyContent="space-between" mt={2}>
            <Dropdown
              name="cardioGoal"
              label="Goal"
              className="base-dropdown"
              value={cardioAgainstWhichGoal}
              onChange={(v) =>
                //setCardioAgainstWhichGoal(v.target.value as "TIME" | "HEART_RATE" | "CALORIES")
                setCardioAgainstWhichGoal(v.target.value as "TIME" | "CALORIES")
              }
            >
              <MenuItem value={"TIME"}>Time</MenuItem>
              {/* <MenuItem value={"HEART_RATE"}>Heart Rate</MenuItem> */}
              <MenuItem value={"CALORIES"}>Calories</MenuItem>
            </Dropdown>

            <ButtonGroup
              size="large"
              disableElevation
              aria-label="medium group"
              className="month-button-group"
            >
              <MButton
                className={`button-group-item left-border-radius ${
                  cardioDateRange === "THIS_MONTH" && `btn-active`
                }`}
                onClick={() => setCardioDateRange("THIS_MONTH")}
              >
                This Month
              </MButton>
              <MButton
                className={`button-group-item ${cardioDateRange === "LAST_MONTH" && `btn-active`}`}
                onClick={() => setCardioDateRange("LAST_MONTH")}
              >
                Last Month
              </MButton>
              <MButton
                className={`button-group-item right-border-radius ${
                  cardioDateRange === "TWO_MONTHS_AGO" && `btn-active`
                }`}
                onClick={() => setCardioDateRange("TWO_MONTHS_AGO")}
              >
                2 Months Ago
              </MButton>
            </ButtonGroup>
          </Box>

          <Box flex={1} className="report-content-container" mt={2}>
            <ReportTableOptions
              title={cardioDateRangeLabel}
              type="cardio-against-goal"
              showOptions={false}
              onClickPdf={() => {}}
              onClickPrint={() => {}}
              csvData={[]}
            />
            <ResponsiveContainer width="100%" height="80%">
              <ComposedChart
                width={500}
                height={300}
                data={cardioGraphData}
                margin={{
                  bottom: 20,
                  left: 20,
                  right: 20,
                  top: 20,
                }}
              >
                <CartesianGrid horizontal={true} vertical={false} stroke="#f5f5f5" />
                <XAxis
                  axisLine={false}
                  dataKey="name"
                  scale="auto"
                  tick={{ fill: "#313A3D", fontSize: 12 }}
                />
                <YAxis axisLine={false} tick={{ fill: "#313A3D", fontSize: 12 }} />
                <Tooltip />
                <Legend content={renderLegend} />
                <Bar dataKey="daily" stackId="a" barSize={8} fill="#739FE7" radius={20} />
                <Bar dataKey="cumulative" stackId="a" barSize={8} fill="#78C553" radius={20} />
                {/* <Line
                  type="monotone"
                  strokeWidth={2}
                  dataKey="minTarget"
                  stroke="#FF825A"
                  dot={false}
                />
                <Line
                  type="monotone"
                  strokeWidth={2}
                  dataKey="maxTarget"
                  stroke="#BB7FFF"
                  dot={false}
                /> */}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        <Box flex={1}>
          <p className="section-title">Strength Against Goal</p>
          <Box flexDirection="row" display="flex" justifyContent="space-between" mt={2}>
            {/* <Dropdown
              name="strengthProgram"
              label="Strength Program"
              className="base-dropdown"
              value={strengthAgainstWhichProgram}
              onChange={(v) =>
                setStrengthAgainstWhichProgram(v.target.value as "TIME" | "HEART_RATE" | "CALORIES")
              }
            >
              {workoutPlan?.strength_workouts?.map((m, i) => {
                if (m.is_active) {
                  return (
                    <MenuItem key={`strengthProgram${i}`} value={m.id}>
                      {m.name}
                    </MenuItem>
                  );
                }
                return null;
              })}
              <MenuItem value={"-1"}>Logged from Web</MenuItem>
            </Dropdown> */}

            <ButtonGroup
              size="large"
              disableElevation
              aria-label="medium group"
              className="month-button-group"
            >
              <MButton
                className={`button-group-item left-border-radius ${
                  strengthDateRange === "THIS_MONTH" && `btn-active`
                }`}
                onClick={() => setStrengthDateRange("THIS_MONTH")}
              >
                This Month
              </MButton>
              <MButton
                className={`button-group-item ${
                  strengthDateRange === "LAST_MONTH" && `btn-active`
                }`}
                onClick={() => setStrengthDateRange("LAST_MONTH")}
              >
                Last Month
              </MButton>
              <MButton
                className={`button-group-item right-border-radius ${
                  strengthDateRange === "TWO_MONTHS_AGO" && `btn-active`
                }`}
                onClick={() => setStrengthDateRange("TWO_MONTHS_AGO")}
              >
                2 Months Ago
              </MButton>
            </ButtonGroup>
          </Box>

          <Box flex={1} className="report-content-container" mt={2}>
            <ReportTableOptions
              title={strengthDateRangeLabel}
              type="strength-against-goal"
              showOptions={false}
              onClickPdf={() => {}}
              onClickPrint={() => {}}
              csvData={[]}
            />
            <ResponsiveContainer width="100%" height="80%">
              <ComposedChart
                width={500}
                height={300}
                data={strengthGraphData}
                margin={{
                  bottom: 20,
                  left: 20,
                  right: 20,
                  top: 20,
                }}
              >
                <CartesianGrid horizontal={true} vertical={false} stroke="#f5f5f5" />
                <XAxis
                  axisLine={false}
                  dataKey="name"
                  scale="auto"
                  tick={{ fill: "#313A3D", fontSize: 12 }}
                />
                <YAxis axisLine={false} tick={{ fill: "#313A3D", fontSize: 12 }} />
                <Tooltip />
                <Legend content={renderLegend} />
                <Bar dataKey="daily" stackId="a" barSize={8} fill="#739FE7" radius={20} />
                <Bar dataKey="cumulative" stackId="a" barSize={8} fill="#78C553" radius={20} />
                {/* <Line
                  type="monotone"
                  strokeWidth={2}
                  dataKey="minTarget"
                  stroke="#FF825A"
                  dot={false}
                />
                <Line
                  type="monotone"
                  strokeWidth={2}
                  dataKey="maxTarget"
                  stroke="#BB7FFF"
                  dot={false}
                /> */}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={4}>
        <p className="section-title">Notes</p>
        <Link href="#" className="note-save-button">
          Save
        </Link>
      </Stack>

      <textarea className="member-summary-notes"></textarea>
    </Box>
  );
};

const renderLegend = (props) => {
  const { payload } = props;

  return (
    <Stack direction="row" justifyContent="space-evenly" alignItems="center" mt={1}>
      {payload.map((entry, index) => {
        let bg = "";
        let title = "";
        if (entry.value === "daily") {
          bg = "#739FE7";
          title = "Daily data";
        } else if (entry.value === "cumulative") {
          bg = "#78C553";
          title = "Cumulative";
        } else if (entry.value === "minTarget") {
          bg = "#BB7FFF";
          title = "Min Target";
        } else if (entry.value === "maxTarget") {
          bg = "#FF825A";
          title = "Max Target";
        }
        if (entry.value.includes("Target")) {
          return (
            <Stack flex={1} direction="row" key={`item-${index}`} gap={1} alignItems="center">
              <Box className="legend-bar-view" bgcolor={bg}></Box>
              {title}
            </Stack>
          );
        }
        return (
          <Stack flex={1} direction="row" key={`item-${index}`} gap={1} alignItems="center">
            <Box className="legend-circle-view" bgcolor={bg}></Box>
            {title}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default MemberSummaryComp;
